import React, { useCallback, useState } from "react";
import { db } from "@/db";
import { PostType, PostTypeFromSearch } from "@/core/types/contentTypes";
import { useOfflineAudioContent } from "@/hooks/indexeddb/useOfflineAudioContent";
import { wrapper } from "@/core/store/store";
import PostsApi from "@/connectors/fcWordpress/apiCallers/PostsApi";
import { toast } from "sonner";
import { Toast } from "@/components/ui-kit/Toast";
import { useAppSelector } from "@/core/store/reduxHooks";
import { getUser } from "@/core/store/app/AppSlice";
import parse, { Element } from "html-react-parser";
import { useOfflineImageContent } from "@/hooks/indexeddb/useOfflineImageContent";
import { useOfflineGalleryContent } from "@/hooks/indexeddb/useOfflineGalleryContent";
import PostContentApi from "@/connectors/fcWordpress/apiCallers/PostContentApi";

type OfflinePostContent = {
  downloadPost: (postId: number | string, withPost?: boolean) => Promise<PostType | undefined>;
  getDownloadedPost: (id: number) => Promise<PostType | undefined>;
  deletePost: (id: number, withPost?: boolean) => void;
  downloading: boolean;
  saveOrDelete: (post: PostType | PostTypeFromSearch) => Promise<undefined | boolean>;
  isDownloaded: (id: number) => Promise<boolean>;
  saveMissing: (posts: PostType[]) => Promise<boolean>;
  downloadAllBookmarked: () => Promise<void>;
  deleteAll: () => Promise<void>;
  deleteAllFrom: (posts: PostType[]) => Promise<boolean>;
  offlinePostThumbnail: (post: PostType | PostTypeFromSearch) => Promise<string | undefined>;
  deleteAllFromByIds: (posts: number[]) => Promise<boolean>;
};

export const useOfflinePostContent = (): OfflinePostContent => {
  const { store } = wrapper.useWrappedStore({});
  const user = useAppSelector(getUser);
  const [downloading, setDownloading] = useState(false);
  const { downloadTrack, isDownloaded: isAudioDownloaded, deleteTrack } = useOfflineAudioContent();
  const { downloadGallery, deleteGallery } = useOfflineGalleryContent();
  const { saveImage } = useOfflineImageContent();

  const downloadPost = async (postId: number | string, withToast = true) => {
    if (withToast) {
      toast.custom((t) => <Toast id={t} text="Probíhá stahování..." withLoader />, {
        // Unfortunately, we cannot set duration to infinity, so we have to set it to a very high number
        duration: 1000000,
        id: postId.toString(),
      });
    }

    try {
      const api = new PostsApi({
        id: postId.toString(),
        language: process.env.NEXT_PUBLIC_DEFAULT_ROUTING_LANGUAGE_CODE!,
      });

      const contentAPI = new PostContentApi({
        id: postId.toString(),
        language: process.env.NEXT_PUBLIC_DEFAULT_ROUTING_LANGUAGE_CODE!,
      });
      await contentAPI.fetchPostContentById();

      await api.fetchSinglePostById();
      let fullPost = api.result as PostType;

      //Modify post
      fullPost.content = contentAPI.result;
      delete fullPost.relatedArticles;
      delete fullPost.tags;
      delete fullPost.meta;
      delete fullPost.metaContent;
      delete fullPost.yoastHeadJson;

      await db.posts.put({ id: fullPost.id, post: fullPost }, fullPost.id);
      //await precacheUrl(fullPost);
      await savePostImages(fullPost);

      if (fullPost.hasGallery && fullPost.galleryCount) {
        await downloadGallery(fullPost);
      }

      if (fullPost.audio?.url) {
        await downloadTrack(fullPost, false);
      }

      if (withToast) {
        toast?.dismiss(postId.toString());
        toast.custom((t) => <Toast id={t} text="Stahování dokončeno" showRedirectToDownloaded />, {
          duration: 3000,
        });
      }
      return fullPost;
    } catch (error) {
      if (withToast) {
        toast?.dismiss(postId.toString());
        toast.custom((t) => <Toast id={t} text="Stahování se nepodařilo" />, {
          duration: 2000,
        });
      }
    }

    return;
  };

  const savePostImages = async (post: PostType) => {
    await saveImage(post?.thumbnail["full"]?.url!, post.id);

    post.authors?.map(async (author) => {
      await saveImage(author.image?.url!, post.id);
    });

    parse(post.content!, {
      replace: async (domNode) => {
        if (!domNode.hasOwnProperty("attribs")) {
          return;
        }
        const domNodeElement = domNode as Element;
        if (domNodeElement.attribs.class?.includes("wp-block-image")) {
          if ((domNodeElement.children[0] as Element)?.name === "img") {
            await saveImage((domNodeElement.children[0] as Element).attribs.src, post.id);
          }
        }
        return;
      },
    });
  };

  const offlinePostThumbnail = async (post: PostType | PostTypeFromSearch) => {
    const record = await db.imageSources.where("originalUrl").equalsIgnoreCase(post.thumbnail["full"].url).first();

    if (navigator.onLine) {
      return post.thumbnail["full"].url;
    }

    if (record?.source) {
      return URL.createObjectURL(new Blob([record.source]));
    }
    return;
  };

  const precacheUrl = async (post: PostType) => {
    if ("caches" in window) {
      caches.open("others").then((cache) => {
        const url = `/${post.id}/${post.slug}`;
        cache.add(url);
      });
    }
  };

  const getDownloadedPost = useCallback(async (id: number) => {
    const post = await db.posts.get(id);
    return post?.post;
  }, []);

  const deletePost = async (id: number, withToast = true) => {
    if (withToast) {
      toast.custom((t) => <Toast id={t} duration={3000} text="Probíhá mazání..." />, {
        duration: 3000,
        onAutoClose() {
          toast.custom((t) => <Toast id={t} text="Všechny soubory byly smazány" />, {
            duration: 2000,
          });
        },
      });
    }

    if (await isAudioDownloaded(id)) {
      await deleteTrack(id);
    }
    await deleteGallery(id);

    await db.imageSources.where("postId").equals(id).delete();
    return db.posts.delete(id);
  };

  const saveMissing = async (posts: PostType[]) => {
    try {
      for await (let post of posts) {
        if (!(await isDownloaded(post.id))) {
          await downloadPost(post.id, false);
        }
      }

      return true;
    } catch {
      return false;
    }
  };

  const downloadAllBookmarked = async () => {
    if (!user) {
      alert("Pokud si chcete článek, který vás zaujal uložit na později, je třeba být přihlášen");
      toast.custom(
        (t) => (
          <Toast
            id={t}
            duration={5000}
            text="Pokud si chcete článek, který vás zaujal uložit na později, je třeba být přihlášen"
          />
        ),
        {}
      );
      return;
    }

    toast.custom((t) => <Toast id={t} text="Probíhá stahování..." withLoader />, {
      duration: 100000,
      id: "allBookmarked",
    });

    user?.user_meta.bookmarks.map(async (id) => {
      await downloadPost(id, false);
    });

    toast.dismiss("allBookmarked");
    toast.custom((t) => <Toast id={t} text="Stahování dokončeno" showRedirectToDownloaded />, {
      duration: 2000,
    });

    return;
  };

  const deleteAll = async () => {
    toast.custom((t) => <Toast id={t} duration={3000} text="Probíhá mazání..." />, {
      duration: 3000,
      onAutoClose() {
        toast.custom((t) => <Toast id={t} text="Mazání dokončeno" />, {
          duration: 2000,
        });
      },
    });

    db.posts.clear();
    db.issues.clear();
    db.audioProgress.clear();
    db.audioSources.clear();
    return;
  };

  const deleteAllFrom = async (posts: PostType[]) => {
    try {
      for await (let post of posts) {
        await deletePost(post.id, false);
      }
      return true;
    } catch {
      return false;
    }
  };

  const deleteAllFromByIds = async (posts: number[]) => {
    try {
      for await (let post of posts) {
        await deletePost(post, false);
      }
      return true;
    } catch {
      return false;
    }
  };

  const saveOrDelete = async (post: PostType | PostTypeFromSearch) => {
    if (!!(await getDownloadedPost(post.id))) {
      deletePost(post.id);
      return;
    }

    await downloadPost(post.id);
    return true;
  };

  const isDownloaded = async (id: number) => {
    const post = await getDownloadedPost(id);
    return !!post;
  };

  return {
    downloadPost,
    getDownloadedPost,
    deletePost,
    downloading,
    saveOrDelete,
    isDownloaded,
    saveMissing,
    downloadAllBookmarked,
    deleteAll,
    deleteAllFrom,
    deleteAllFromByIds,
    offlinePostThumbnail,
  };
};
